import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import type { PatientSearchItem } from "@elation/api-client/src/stitch-web/models";

import { navigationV2 } from "@src/shared/navigation/v2";
import { captureException } from "@src/shared/utils/observability/sentry";

import {
  PatientSearchResultUnassignableError,
  useResolvePatientId,
} from "../../../../../application";
import { PatientEntity } from "../../../../../domain";

export function usePatientOpener(navigateStateSource: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isResolvingPatientId, resolvePatientId } = useResolvePatientId();
  const isOpeningPatientRef = useRef(false);
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  const openPatient = useCallback(
    async (patient: PatientSearchItem) => {
      if (isOpeningPatientRef.current || isResolvingPatientId) return;

      isOpeningPatientRef.current = true;
      setOpeningKey(PatientEntity.searchResultKey(patient));

      try {
        const navState = {
          source: navigateStateSource,
          from: `${location.pathname}${location.search}`,
        };

        const patientId = await resolvePatientId(patient);

        navigate(navigationV2.patientDetail(String(patientId)), {
          state: navState,
        });
      } catch (error) {
        if (error instanceof PatientSearchResultUnassignableError) {
          toast.error("Unable to open this patient.");
          return;
        }

        captureException(error, {
          tags: {
            feature: "patients-v2",
            action: "open-patient-search-result",
          },
          extra: {
            ehr: patient.ehr,
            externalPatientId: patient.externalPatientId,
            patientId: patient.patientId,
          },
        });
        toast.error("Unable to open this patient. Please try again.");
      } finally {
        isOpeningPatientRef.current = false;
        setOpeningKey(null);
      }
    },
    [
      location.pathname,
      location.search,
      navigate,
      navigateStateSource,
      isResolvingPatientId,
      resolvePatientId,
    ],
  );

  return {
    openPatient,
    openingKey,
    isOpening: isResolvingPatientId || openingKey != null,
  } as const;
}
